import { css } from '@emotion/react'
import { Alert, Button, Cell, FormControl, Grid, HFlow, Icon, Link, Theme, Tooltip, VFlow } from 'bold-ui'
import {
  CheckboxField,
  ErrorField,
  Form,
  FormRenderProps,
  getDefaultOnSubmitFailed,
  NumberField,
  PasswordField,
} from 'components/form'
import { InfoIcon } from 'components/InfoIcon'
import theme from 'config/theme'
import { useFlags } from 'config/useFlagsContext'
import { FormState } from 'final-form'
import { useValidarConfiguracaoCertificadoMutation } from 'graphql/hooks.generated'
import { ServicosCertificadoEnum } from 'graphql/types.generated'
import { useMemo, useState } from 'react'
import { metaPath } from 'util/metaPath'
import { handleKeyDown } from 'view/atendimentos/detail/soap/util-soap'

import { CertificadoSelectField } from './CertificadoSelectField'
import ConfiguracaoCertificadosFormModel from './ConfiguracoesCertificadosRootView'
import { InformacaoContraChaveCertificadoComponent } from './InformacaoContraChaveCertificadoComponent'
import { ServicosSelecionadosConfirmacaoModal } from './ServicosSelecionadosConfirmacaoModal'
import { ValidacaoServicosErrorAlert, ValidationErrors } from './ValidacaoServicosErrorAlert'
import { configuracaoCertificadosCreateValidator } from './validator'

export interface ConfiguracaoCertificadoCreateViewProps {
  internetHabilitada: boolean
  handleSubmit: (values: ConfiguracaoCertificadosFormModel) => Promise<void>
  contraChave: string
  loading: boolean
}

const path = metaPath<ConfiguracaoCertificadosFormModel>()
const internetMessage = 'Não pode ser habilitada sem conexão com a internet'
const iconToolTipText = 'Selecione os serviços que serão habilitados com o certificado'
const identificadorSolicitanteTollTipText =
  'O identificador pode ser obtido no programa Implantação e-SUS APS, disponível no e-Gestor APS, consultando o número do "Identificador solicitante" do último certificado digital gerado com status "Ativo".'

export function ConfiguracaoCertificadosCreateView({
  internetHabilitada,
  handleSubmit,
  contraChave,
  loading,
}: ConfiguracaoCertificadoCreateViewProps) {
  const { API_SINAN_ENABLED } = useFlags()
  const [error, setError] = useState<ValidationErrors>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [servicosValidados, setServicosValidados] = useState<ReadonlyArray<ServicosCertificadoEnum>>()
  const [validateConnection] = useValidarConfiguracaoCertificadoMutation()
  const classes = useMemo(() => createStyles(theme), [])

  const renderForm = (formProps: FormRenderProps<ConfiguracaoCertificadosFormModel>) => {
    const handleModalClose = () => {
      setIsModalOpen(false)
    }

    const handleModalSubmit = () => {
      handleSubmit({ ...formProps.values, servicos: servicosValidados })
    }

    return (
      <form noValidate onKeyDown={handleKeyDown}>
        <ServicosSelecionadosConfirmacaoModal
          servicosSelecionados={formProps.values.servicos}
          servicosValidados={servicosValidados}
          isModalOpen={isModalOpen}
          handleModalClose={handleModalClose}
          handleModalSubmit={handleModalSubmit}
          loading={loading}
        />
        <VFlow>
          {error && <ValidacaoServicosErrorAlert error={error} />}
          <VFlow>
            <HFlow style={{ gridGap: '0' }}>
              <FormControl label='Serviços' required style={{ width: 'auto' }}>
                <VFlow>
                  <Tooltip text={!internetHabilitada && internetMessage}>
                    <VFlow>
                      <CheckboxField
                        name={path.servicos}
                        value={ServicosCertificadoEnum.RNDS}
                        label='RNDS'
                        disabled={!internetHabilitada}
                      />
                      <CheckboxField
                        name={path.servicos}
                        value={ServicosCertificadoEnum.CADSUS}
                        label='CADSUS'
                        disabled={!internetHabilitada}
                      />
                      <Tooltip text='Programa de Dignidade Menstrual'>
                        <span>
                          <CheckboxField
                            name={path.servicos}
                            value={ServicosCertificadoEnum.PDM}
                            label='PDM'
                            disabled={!internetHabilitada}
                          />
                        </span>
                      </Tooltip>
                      {API_SINAN_ENABLED && (
                        <Tooltip text='Sistema de Informação de Agravos de Notificação'>
                          <span>
                            <CheckboxField
                              name={path.servicos}
                              value={ServicosCertificadoEnum.SINAN}
                              label='SINAN'
                              disabled={!internetHabilitada}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </VFlow>
                  </Tooltip>
                </VFlow>
              </FormControl>
              <HFlow
                alignItems='flex-start'
                style={{ position: 'absolute', width: '23rem', height: '1.5rem', marginLeft: '4.5rem' }}
              >
                <Tooltip text={iconToolTipText} placement='auto' style={{ display: 'block' }}>
                  <Icon css={classes.iconStyle} icon='infoCircleFilled' size={1} />
                </Tooltip>
              </HFlow>
            </HFlow>
            <ErrorField name={path.servicos} />
          </VFlow>
          <InformacaoContraChaveCertificadoComponent contraChave={contraChave} />
          <Grid alignItems='flex-start'>
            <Cell size={3}>
              <Tooltip text={!internetHabilitada && internetMessage}>
                <CertificadoSelectField
                  name={path.certificadoSelect}
                  label='Certificado'
                  disabled={!internetHabilitada}
                  required
                />
              </Tooltip>
            </Cell>
            <Cell size={3}>
              <Tooltip text={!internetHabilitada && internetMessage}>
                <PasswordField
                  name={path.senhaCertificado}
                  label='Senha'
                  required
                  disabled={!internetHabilitada}
                  maxLength={255}
                />
              </Tooltip>
            </Cell>

            {formProps.values.servicos?.includes(ServicosCertificadoEnum.RNDS) && (
              <Cell size={4}>
                <HFlow hSpacing={0.25}>
                  <FormControl label='Identificador solicitante fornecido pela RNDS' required />
                  <InfoIcon icon='infoCircleFilled' text={identificadorSolicitanteTollTipText} />
                </HFlow>
                <Tooltip text={!internetHabilitada && internetMessage}>
                  <NumberField
                    name={path.idSolicitante}
                    disabled={!internetHabilitada}
                    maxLength={20}
                    style={{ width: '90%' }}
                    required
                  />
                </Tooltip>
              </Cell>
            )}

            <Cell size={2} style={{ marginTop: '1.6rem' }}>
              <Tooltip text={!internetHabilitada && internetMessage}>
                <Button
                  kind='primary'
                  size='small'
                  disabled={!internetHabilitada || loading}
                  onClick={formProps.handleSubmit}
                  loading={loading}
                >
                  Habilitar
                </Button>
              </Tooltip>
            </Cell>
          </Grid>
        </VFlow>
      </form>
    )
  }

  const innerHandleSubmit = ({
    servicos,
    senhaCertificado,
    idSolicitante,
    certificadoSelect,
  }: ConfiguracaoCertificadosFormModel) => {
    return validateConnection({
      variables: {
        configuracaoCertificadoInput: {
          servicos: servicos,
          senhaCertificado: senhaCertificado,
          pathCertificado: certificadoSelect.path,
          idSolicitante: idSolicitante,
        },
      },
    }).then(({ data: { validarConfiguracaoCertificado } }) => {
      setServicosValidados(validarConfiguracaoCertificado)
      setError(null)
      if (servicos.every((item) => validarConfiguracaoCertificado.includes(item))) {
        handleSubmit({
          servicos: servicos,
          senhaCertificado: senhaCertificado,
          idSolicitante: idSolicitante,
          certificadoSelect: certificadoSelect,
        })
      } else {
        setIsModalOpen(true)
      }
    })
  }

  const onSubmitFailed = (formState: FormState<ConfiguracaoCertificadosFormModel>) => {
    getDefaultOnSubmitFailed<ConfiguracaoCertificadosFormModel>()(formState)
    setError(() => (formState.hasValidationErrors ? undefined : formState.submitErrors))
  }

  return (
    <VFlow>
      <Alert type='info' inline>
        <div>
          <b>1.</b>
          {' Faça o download do certificado do município através da área restrita do '}
          <Link href='https://acesso-egestoraps.saude.gov.br/login' target='_blank'>
            e-Gestor AB
          </Link>
          .
        </div>
        <div>
          <b>2.</b>
          {' O administrador da instalação deve incluir o certificado (arquivo .pfx ou .p12) ' +
            'com a chave privada na pasta "chaves" no instalador do PEC (no Windows: c:Program ' +
            'Files/e-SUS/webserver/chaves e no Linux: /opt/e-SUS/webserver/chaves).'}
        </div>

        <div>
          <b>3.</b>
          {' Após incluído no servidor, selecione o certificado no campo ' +
            '"certificado" e insira a senha e o identificador solicitante para habilitar.'}
        </div>
      </Alert>
      <Form<ConfiguracaoCertificadosFormModel>
        render={renderForm}
        onSubmit={innerHandleSubmit}
        onSubmitFailed={onSubmitFailed}
        validate={configuracaoCertificadosCreateValidator}
        validateOnBlur={false}
      />
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  iconStyle: css`
    display: block;
    &:hover {
      color: ${theme.pallete.primary.main};
    }
  `,
})
